import React, {
  Fragment,
  createContext,
  useContext,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router';
import { styled } from '@mui/material/styles';
import useBreadcrumbs from '../../../vendor/use-react-router-breadcrumbs';

type BreadcrumbRoutes = Record<string, React.ComponentType<any>>;

type BreadcrumbsContextValue = {
  routes: BreadcrumbRoutes;
  registerRoutes: (newRoutes: BreadcrumbRoutes) => void;
};

// Create a context for breadcrumb routes
const BreadcrumbsContext = createContext<BreadcrumbsContextValue>({
  routes: {},
  registerRoutes: () => {},
});

// Custom hook to access and register breadcrumb routes
export const useBreadcrumbRoutes = () => {
  return useContext(BreadcrumbsContext);
};

// Provider component to manage breadcrumb routes state
export const BreadcrumbsProvider = ({ children }) => {
  const [routes, setRoutes] = useState({});

  const registerRoutes = useCallback((newRoutes) => {
    setRoutes((prevRoutes) => {
      return {
        ...prevRoutes,
        ...newRoutes,
      };
    });
  }, []);

  return (
    <BreadcrumbsContext.Provider value={{ routes, registerRoutes }}>
      {children}
    </BreadcrumbsContext.Provider>
  );
};

BreadcrumbsProvider.propTypes = {
  children: PropTypes.node,
};

const BreadcrumbsContainer = styled('div')(({ theme }) => {
  return {
    // Soft brand tint under the green AppBar so the chrome doesn't go
    // straight from teal → pure white.
    backgroundColor: theme.palette.primary.light,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
  };
});

const BreadcrumbItem = styled('span')(({ theme }) => {
  return {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.5,
  };
});

const BreadcrumbLink = styled(Link)(({ theme }) => {
  return {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.5,
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  };
});

const Breadcrumbs = () => {
  const location = useLocation();
  const { routes } = useBreadcrumbRoutes();
  const breadcrumbs = getBreadCrumbsFromRoutes(location, routes);

  return (
    <BreadcrumbsContainer>
      {breadcrumbs.map(({ match, breadcrumb }, index) => {
        const isLast = index === breadcrumbs.length - 1;

        return (
          <Fragment key={match.pathname}>
            {isLast ? (
              <BreadcrumbItem>{breadcrumb}</BreadcrumbItem>
            ) : (
              <BreadcrumbLink to={match.pathname}>{breadcrumb}</BreadcrumbLink>
            )}
            {index < breadcrumbs.length - 1 && (
              <BreadcrumbItem> » </BreadcrumbItem>
            )}
          </Fragment>
        );
      })}
    </BreadcrumbsContainer>
  );
};

// Update to use passed routes rather than static property
function getBreadCrumbsFromRoutes(location, routes) {
  const keys = Object.keys(routes);

  if (keys.length === 0) {
    return useBreadcrumbs().slice(1); // Remove Home element
  }

  const routesConfig = Object.keys(routes).map((path) => {
    const breadcrumb = routes[path];
    return { path, breadcrumb };
  });

  const breadcrumbs = useBreadcrumbs(routesConfig).slice(1); // Remove Home element
  return breadcrumbs;
}

export default Breadcrumbs;
